import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import axios from 'axios'
import {
  PiArrowCounterClockwise,
  PiChatCenteredSlash,
  PiPlus,
  PiTrash,
} from 'react-icons/pi'
import to from 'await-to-js'

import Checkbox from 'components/Checkbox/checkbox'
import FbButton from 'components/FbUI/FbButton'
import { Toggle } from 'components/FbUI/Toggle'
import { FbDropdown } from 'components/FbUI/FbDropdown'
import {
  AutoUnsubscribeMode,
  AutoUnsubscribePattern,
  useGetGlobalCampaignSettings,
  useUpdateGlobalCampaignSettings,
} from 'features/campaigns/global_settings_api'

import {
  arePatternListsEqual,
  findDuplicateError,
  validateRow,
} from './autoUnsubscribeValidation'

const MODE_OPTIONS: { value: AutoUnsubscribeMode; label: string }[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'whole_reply', label: 'Whole reply' },
  { value: 'regex', label: 'Regex' },
]

// Rows carry a client-only key so React can track identity across edits and
// potential future reorder. The key is stripped before sending to the backend.
type PatternRow = AutoUnsubscribePattern & { _key: string }

const stripKey = (row: PatternRow): AutoUnsubscribePattern => ({
  pattern: row.pattern,
  mode: row.mode,
  enabled: row.enabled,
})

const makeKey = (): string => crypto.randomUUID()

const toRows = (items: AutoUnsubscribePattern[]): PatternRow[] =>
  items.map((p) => ({ ...p, _key: makeKey() }))

export function AutoUnsubscribeSettings() {
  const { data: settings } = useGetGlobalCampaignSettings()
  const updateMutation = useUpdateGlobalCampaignSettings()

  // Memoize so identity is stable across renders (lets downstream useMemo
  // dependencies short-circuit when settings is unchanged).
  const serverPatterns = useMemo(
    () => settings?.auto_unsubscribe_patterns ?? [],
    [settings?.auto_unsubscribe_patterns]
  )
  const serverEnabled = settings?.auto_unsubscribe_enabled ?? true
  const defaultPatterns = useMemo(
    () => settings?.default_auto_unsubscribe_patterns ?? [],
    [settings?.default_auto_unsubscribe_patterns]
  )

  const [patternsDraft, setPatternsDraft] = useState<PatternRow[] | null>(null)
  const [enabledDraft, setEnabledDraft] = useState<boolean | null>(null)

  // Server rows re-keyed only when the server payload changes — safe because
  // we only render by key while the user hasn't started editing (draft is null).
  const serverRows = useMemo(() => toRows(serverPatterns), [serverPatterns])
  const patterns: PatternRow[] = patternsDraft ?? serverRows
  const enabled = enabledDraft ?? serverEnabled

  const plainPatterns = useMemo(() => patterns.map(stripKey), [patterns])
  const rowErrors = useMemo(
    () => plainPatterns.map(validateRow),
    [plainPatterns]
  )
  const duplicateError = useMemo(
    () => findDuplicateError(plainPatterns),
    [plainPatterns]
  )

  const hasErrors = rowErrors.some(Boolean) || Boolean(duplicateError)
  const patternsDirty =
    patternsDraft !== null &&
    !arePatternListsEqual(plainPatterns, serverPatterns)
  const enabledDirty = enabledDraft !== null && enabledDraft !== serverEnabled
  const isDirty = patternsDirty || enabledDirty

  const updateRow = (
    index: number,
    partial: Partial<AutoUnsubscribePattern>
  ) => {
    setPatternsDraft((draft) => {
      const base = draft ?? serverRows
      return base.map((row, i) => (i === index ? { ...row, ...partial } : row))
    })
  }

  const removeRow = (index: number) => {
    setPatternsDraft((draft) => {
      const base = draft ?? serverRows
      return base.filter((_, i) => i !== index)
    })
  }

  const addRow = () => {
    setPatternsDraft((draft) => {
      const base = draft ?? serverRows
      return [
        ...base,
        { pattern: '', mode: 'contains', enabled: true, _key: makeKey() },
      ]
    })
  }

  const restoreDefaults = () => {
    setPatternsDraft(toRows(defaultPatterns))
  }

  const handleSave = async () => {
    if (hasErrors || !isDirty) return
    const payload: {
      auto_unsubscribe_enabled?: boolean
      auto_unsubscribe_patterns?: AutoUnsubscribePattern[]
    } = {}
    if (patternsDirty) payload.auto_unsubscribe_patterns = plainPatterns
    if (enabledDirty) payload.auto_unsubscribe_enabled = enabled

    const [err] = await to(updateMutation.mutateAsync(payload))
    if (err) {
      const fallback = 'Failed to save auto-unsubscribe settings'
      const detail = axios.isAxiosError<{ detail?: string }>(err)
        ? err.response?.data?.detail
        : undefined
      toast.error(detail ?? fallback)
      return
    }

    setPatternsDraft(null)
    setEnabledDraft(null)
    toast.success('Auto-unsubscribe settings saved')
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-start gap-3 flex-1">
          <div className="size-10 shrink-0 rounded-lg bg-brand-active/10 flex items-center justify-center">
            <PiChatCenteredSlash size={20} className="text-brand-active" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <h3 className="text-lg font-semibold text-fg">
                Reply opt-out detection
              </h3>
              <span className="inline-flex items-center rounded-full bg-[#f3f4f6] px-2.5 py-0.5 text-xs font-medium text-[#374151] tracking-[-0.2px]">
                Inbound
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              When a contact replies with any of the phrases below, unsubscribe
              them from campaign emails and mark them as Do Not Contact.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 pt-2">
          <span className="text-xs text-gray-500">
            {enabled ? 'On' : 'Off'}
          </span>
          <Toggle
            aria-label="Enable reply opt-out detection"
            checked={enabled}
            onChange={setEnabledDraft}
          />
        </div>
      </div>

      <div className={enabled ? '' : 'opacity-50 pointer-events-none'}>
        {patterns.length > 0 && (
          <div className="flex items-center gap-2 mb-2 px-1" aria-hidden>
            <div className="w-4 shrink-0" />
            <span className="w-[140px] shrink-0 text-sm font-medium text-gray-700 tracking-[-0.2px]">
              Match type
            </span>
            <span className="flex-1 text-sm font-medium text-gray-700 tracking-[-0.2px]">
              Phrase or pattern
            </span>
            <div className="w-9 shrink-0" />
          </div>
        )}

        <div className="space-y-3">
          {patterns.length === 0 && (
            <p className="text-xs text-gray-500 italic py-2">
              No rules configured. Add one below or restore the recommended
              defaults.
            </p>
          )}

          {patterns.map((row, index) => (
            <div key={row._key} className="flex items-start gap-2">
              <div className="w-4 h-11 shrink-0 flex items-center">
                <Checkbox
                  aria-label="Rule enabled"
                  checked={row.enabled}
                  onChange={(e) =>
                    updateRow(index, { enabled: e.target.checked })
                  }
                />
              </div>
              <div className="w-[140px] shrink-0">
                <FbDropdown
                  label={
                    MODE_OPTIONS.find((opt) => opt.value === row.mode)?.label ??
                    'Match type'
                  }
                  items={MODE_OPTIONS.map((opt) => ({
                    key: opt.value,
                    label: opt.label,
                  }))}
                  selectedKey={row.mode}
                  onAction={(key) =>
                    updateRow(index, { mode: key as AutoUnsubscribeMode })
                  }
                  className="w-full !justify-between"
                />
              </div>
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  aria-label="Phrase or pattern"
                  value={row.pattern}
                  placeholder="e.g. unsubscribe"
                  maxLength={200}
                  onChange={(e) =>
                    updateRow(index, { pattern: e.target.value })
                  }
                  className="w-full h-11 px-4 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                />
                {rowErrors[index] && (
                  <p className="text-xxs text-error mt-1">{rowErrors[index]}</p>
                )}
              </div>
              <button
                type="button"
                aria-label="Remove rule"
                onClick={() => removeRow(index)}
                className="w-9 h-11 shrink-0 flex items-center justify-center text-gray-500 hover:text-error"
              >
                <PiTrash size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {duplicateError && (
        <p className="text-xxs text-error mt-2">{duplicateError}</p>
      )}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-line">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-1.5 text-sm font-medium text-brand-active hover:text-brand-bright"
          >
            <PiPlus size={14} />
            Add rule
          </button>
          <button
            type="button"
            onClick={restoreDefaults}
            className="flex items-center gap-1.5 text-sm font-medium text-brand-active hover:text-brand-bright"
          >
            <PiArrowCounterClockwise size={14} />
            Restore defaults
          </button>
        </div>
        <FbButton
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!isDirty || hasErrors || updateMutation.isPending}
          loading={updateMutation.isPending}
          className="!bg-brand-bright !text-white hover:!bg-brand-active disabled:!bg-gray-200 disabled:!text-gray-500"
        >
          Save
        </FbButton>
      </div>
    </div>
  )
}
